import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, Plus, Search, X, Globe, MapPin, Users,
  Briefcase, ChevronRight, Loader2
} from "lucide-react";

const emptyCompany = { company_name: "", website: "", country: "", city: "", industry: "", logo: "", notes: "", source: "manual", brand_primary_color: "", brand_secondary_color: "", theme_mode: "dark", legal_company_name: "", legal_form: "", managing_director: "", registration_court: "", commercial_register_number: "", vat_id: "", tax_id: "", billing_address: "", billing_email: "", payment_terms: "", currency: "EUR", bank_name: "", iban: "", bic: "", account_holder: "" };

export default function CompaniesPage() {
  const { token } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCountry, setFilterCountry] = useState("");
  const [filterIndustry, setFilterIndustry] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ ...emptyCompany });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/companies`, { headers: { Authorization: `Bearer ${token}` } });
      setCompanies(res.data);
    } catch { toast.error("Failed to load companies"); }
    finally { setLoading(false); }
  };

  const countries = [...new Set(companies.map(c => c.country).filter(Boolean))].sort();
  const industries = [...new Set(companies.map(c => c.industry).filter(Boolean))].sort();

  const filtered = companies.filter(c => {
    if (search && !c.company_name.toLowerCase().includes(search.toLowerCase()) && !c.city.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterCountry && c.country !== filterCountry) return false;
    if (filterIndustry && c.industry !== filterIndustry) return false;
    return true;
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.company_name) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/companies`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Company created");
      setShowCreate(false);
      setForm({ ...emptyCompany });
      fetchData();
    } catch { toast.error("Failed to create company"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#C9A96E]" /></div>;

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-white">Companies</h1>
          <p className="text-white/40 text-sm mt-1">{companies.length} companies</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-accent flex items-center gap-2" data-testid="create-company-btn">
          <Plus className="w-4 h-4" /> New Company
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search companies..." className="form-input pl-10 w-full" data-testid="companies-search" />
        </div>
        <select value={filterCountry} onChange={e => setFilterCountry(e.target.value)} className="form-input w-auto min-w-[140px]">
          <option value="">All Countries</option>
          {countries.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterIndustry} onChange={e => setFilterIndustry(e.target.value)} className="form-input w-auto min-w-[140px]">
          <option value="">All Industries</option>
          {industries.map(i => <option key={i} value={i}>{i}</option>)}
        </select>
      </div>

      <div className="space-y-2" data-testid="companies-list">
        {filtered.length === 0 && (
          <div className="glass-card-static p-12 text-center">
            <Building2 className="w-12 h-12 text-white/10 mx-auto mb-3" />
            <p className="text-white/40">No companies found</p>
          </div>
        )}
        {filtered.map(company => (
          <Link key={company.id} to={`/companies/${company.id}`} className="block" data-testid={`company-row-${company.id}`}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card-static p-4 flex items-center gap-4 hover:bg-white/[0.04] transition-colors cursor-pointer group">
              <div className="w-10 h-10 rounded-xl bg-white/[0.06] flex items-center justify-center shrink-0 overflow-hidden">
                {company.logo ? <img src={company.logo} alt="" className="w-full h-full object-contain" /> : <Building2 className="w-5 h-5 text-white/30" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{company.company_name}</p>
                <p className="text-white/40 text-sm truncate">{[company.city, company.country].filter(Boolean).join(", ")}{company.industry ? ` · ${company.industry}` : ""}</p>
              </div>
              <div className="hidden md:flex items-center gap-4 text-white/30 text-sm">
                <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{company.contacts_count}</span>
                <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{company.deals_count}</span>
              </div>
              <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-white/50 transition-colors" />
            </motion.div>
          </Link>
        ))}
      </div>

      {/* Create Panel */}
      <AnimatePresence>
        {showCreate && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={() => setShowCreate(false)} />
            <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }} className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-[#0A0A0A] border-l border-white/10 z-50 overflow-y-auto" data-testid="create-company-panel">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-display text-xl text-white">New Company</h2>
                  <button onClick={() => setShowCreate(false)} className="p-2 text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div><label className="form-label">Company Name *</label><input value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))} className="form-input" required data-testid="company-name-input" /></div>
                  <div><label className="form-label">Website</label><input value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} className="form-input" placeholder="https://..." data-testid="company-website" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="form-label">City</label><input value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} className="form-input" /></div>
                    <div><label className="form-label">Country</label><input value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value }))} className="form-input" /></div>
                  </div>
                  <div><label className="form-label">Industry</label><input value={form.industry} onChange={e => setForm(p => ({ ...p, industry: e.target.value }))} className="form-input" placeholder="e.g. Hospitality" /></div>
                  <div><label className="form-label">Notes</label><textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="form-input min-h-[80px]" /></div>
                  <button type="submit" disabled={saving} className="btn-accent w-full flex items-center justify-center gap-2" data-testid="save-company-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create Company
                  </button>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
